import { VideoTemplates } from '@src/components/sidepanel/VideoTemplateSettings/VideoTemplate';
import {
  VIDEO_TEMPLATE_SETTINGS_KEY,
  VideoTemplatesProvider,
} from '@src/components/sidepanel/VideoTemplateSettings/VideoTemplatesContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getElementMetaObject } from '@src/utility/extraElementData';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import styled from 'styled-components';

import SocialProfileRefreshOverlay from '../common/SocialProfileRefreshOverlay';
import Modal from '../Modal';
import { ShareModalPostsContent } from './ShareModalPostsContent';
import { ShareModalTabs } from './ShareModalTabs';

import type {
  ShareableImagePreviewType,
  ShareableImageType,
  VideoClip,
} from '../../types.ts/story';
import type { ShareModalTab } from './ShareModalTabs';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

type Props = {
  onClose: () => void;
  clip?: VideoClip;
  shareable?: ShareableImagePreviewType;
  children: ReactNode;
  selectedDimension: string;
  hideShareButton: boolean;
  imageRef?: React.RefObject<HTMLImageElement>; //to remove
  shareableImageToggle?: Dispatch<SetStateAction<ShareableImageType | null>>;
  posterUrl?: string;
  imageSource?: Record<string, any>;
  videoCreator?: IVideoCreatorStore;
  editorStore?: EditorStoreLike;
};

const ShareModal = observer((props: Props) => {
  const videoCreatorFromContext = useVideoCreatorStore();
  const videoCreator = props.videoCreator || videoCreatorFromContext;
  const { clip, selectedDimension, shareable, editorStore } = props;

  // Determine default tab based on whether clip has active template
  const [activeTab, setActiveTab] = useState<ShareModalTab>(() => {
    if (clip && videoCreator?.currentVideo) {
      const settings = getElementMetaObject(
        videoCreator.currentVideo,
        VIDEO_TEMPLATE_SETTINGS_KEY,
      );
      return settings ? 'templates' : 'posts';
    }
    return 'posts';
  });

  // Check if we should show tabs (only for clips with stores available)
  const showTabs = Boolean(clip && videoCreator && editorStore);

  // Props for posts content
  const postsProps = {
    clip,
    shareable,
    selectedDimension,
    hideShareButton: props.hideShareButton,
    imageRef: props.imageRef,
    shareableImageToggle: props.shareableImageToggle,
    posterUrl: props.posterUrl,
    imageSource: props.imageSource,
  };

  return (
    <Modal
      isOpen
      allowPropagation={false}
      onClose={() => {
        if (videoCreator.showRefreshStoryForSocialProfile) {
          runInAction(
            () => (videoCreator.showRefreshStoryForSocialProfile = false),
          );
        } else {
          props.onClose();
        }
      }}
      paddingVertical="0"
      customWidth="min(80%, 1250px)"
      alwaysShowCancel={videoCreator.showRefreshStoryForSocialProfile}
    >
      <Main data-testid="share-modal">
        {videoCreator.showRefreshStoryForSocialProfile && (
          <SocialProfileRefreshOverlay />
        )}

        <LeftPane>{props.children}</LeftPane>
        <RightPane>
          {showTabs ? (
            <ShareModalTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              hideComments
            >
              {tab =>
                tab === 'templates' ? (
                  <VideoTemplatesProvider
                    videoCreator={videoCreator}
                    editorStore={editorStore!}
                  >
                    <VideoTemplates />
                  </VideoTemplatesProvider>
                ) : (
                  <ShareModalPostsContent {...postsProps} />
                )
              }
            </ShareModalTabs>
          ) : (
            <ShareModalPostsContent {...postsProps} />
          )}
        </RightPane>
      </Main>
    </Modal>
  );
});

export default ShareModal;

const Main = styled.div`
  color: var(--text-secondary);
  padding: 0 0 0 20px;
  text-align: left;
  display: grid;
  grid-template-columns: 56% 44%;
  grid-template-rows: 640px;
  grid-gap: 20px;
`;

const LeftPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 20px 0;
`;

const RightPane = styled.div`
  border-left: 1px solid var(--border);
  padding: 20px;
  padding-right: 0;
  margin-left: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
